import { type FC, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useBilling } from "../../hooks/useBilling";
import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { formatUsd } from "../../utils/currency";

const TOP_UP_AMOUNT_CENTS = 2500;

interface BalanceTone {
  border: string;
  text: string;
}

function getBalanceTone(balance: number): BalanceTone {
  if (balance <= 0) {
    return {
      border: "border-rose-500/40 dark:border-rose-400/30",
      text: "text-rose-600 dark:text-rose-300",
    };
  }
  if (balance <= 5) {
    return {
      border: "border-amber-500/40 dark:border-amber-400/30",
      text: "text-amber-600 dark:text-amber-300",
    };
  }
  return {
    border: "border-slate-300 dark:border-slate-700",
    text: "text-slate-900 dark:text-slate-100",
  };
}

export const CreditBalanceIndicator: FC = () => {
  const { organizationId } = useDashboardAuth();
  const { balance, topUp } = useBilling(organizationId);
  const navigate = useNavigate();
  const [topUpLoading, setTopUpLoading] = useState(false);

  const handleTopUp = useCallback(() => {
    setTopUpLoading(true);
    void topUp(TOP_UP_AMOUNT_CENTS).finally(() => setTopUpLoading(false));
  }, [topUp]);

  if (!organizationId || balance === null) {
    return null;
  }

  const tone = getBalanceTone(balance);

  return (
    <div className={`relative flex items-stretch border ${tone.border}`}>
      <button
        type="button"
        onClick={() => void navigate("/settings?tab=billing")}
        className="group relative min-w-0 flex-1 px-2 py-1.5 text-left transition-colors hover:bg-slate-100/60 dark:hover:bg-slate-800/40"
        aria-label="View billing"
      >
        <p className="flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-500">
          <span aria-hidden className="text-slate-300 dark:text-slate-700">
            [
          </span>
          <span>Credits</span>
          <span aria-hidden className="text-slate-300 dark:text-slate-700">
            ]
          </span>
        </p>
        <p
          className={`mt-0.5 font-mono text-sm font-semibold tabular-nums ${tone.text}`}
        >
          {formatUsd(balance)}
        </p>
      </button>
      <button
        type="button"
        disabled={topUpLoading}
        onClick={handleTopUp}
        aria-label="Top up credits"
        className="flex w-8 flex-shrink-0 items-center justify-center border-l border-slate-300 font-mono text-sm font-bold text-slate-500 transition-colors hover:bg-emerald-500/10 hover:text-emerald-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-500 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-300"
      >
        {topUpLoading ? "…" : "+"}
      </button>
    </div>
  );
};
